import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Chip,
    Paper,
    Button,
} from '@mui/material';
import {
    ChevronRight,
    ExpandMore,
    Folder,
    FolderOpen,
    Business,
    AccountTree,
    Groups,
    Store,
    WorkOutline,
    Add,
    Update,
} from '@mui/icons-material';
import OrganizationService from './OrganizationService';
import { useGlobalStore } from '../../../application/store/store';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../../application/router/routes';

const OrganizationNode = ({
    organization,
    level = 0,
    onOrganizationClick,
    selectedId,
    parentId = null,
}) => {
    const [isExpanded, setIsExpanded] = useState(level < 5); // Auto-expand first 5 levels

    // Updated to handle new data format - check for children array directly
    const hasChildren = organization.children?.length > 0;
    const isSelected = selectedId === organization.id;

    const toggleExpanded = (e) => {
        e.stopPropagation();
        if (hasChildren) {
            setIsExpanded(!isExpanded);
        }
    };

    const handleOrganizationClick = () => {
        // Add parentId to organization object when clicking
        const organizationWithParent = {
            ...organization,
            parentOrganizationId: parentId,
            // Map to expected property names (keeping both for compatibility)
            Id: organization.id,
            Name: organization.name,
            OrganizationType: organization.organizationType,
            ParentOrganizationId: organization.parentOrganizationId,
            Children: organization.children || [],
        };
        onOrganizationClick(organizationWithParent);
    };

    const getTypeIcon = (orgType) => {
        switch (orgType?.toLowerCase()) {
            case 'root':
                return <Business sx={{ fontSize: 16, color: '#d32f2f' }} />;
            case 'default':
                return <Business sx={{ fontSize: 16, color: '#1976d2' }} />;
            case 'asagudarna':
                return <Business sx={{ fontSize: 16, color: '#2e7d32' }} />;
            case 'gudarnas organisation':
                return <Business sx={{ fontSize: 16, color: '#7b1fa2' }} />;
            case 'corporation':
                return <Business sx={{ fontSize: 16, color: '#d32f2f' }} />;
            case 'division':
                return <AccountTree sx={{ fontSize: 16, color: '#3f51b5' }} />;
            case 'department':
                return <WorkOutline sx={{ fontSize: 16, color: '#f57c00' }} />;
            case 'team':
                return <Groups sx={{ fontSize: 16, color: '#00796b' }} />;
            case 'branch':
                return <Store sx={{ fontSize: 16, color: '#c2185b' }} />;
            case 'unit':
                return <WorkOutline sx={{ fontSize: 16, color: '#f9a825' }} />;
            default:
                return <Business sx={{ fontSize: 16, color: '#757575' }} />;
        }
    };

    const getTypeColor = (orgType) => {
        switch (orgType?.toLowerCase()) {
            case 'root':
                return 'error';
            case 'default':
                return 'primary';
            case 'asagudarna':
                return 'success';
            case 'gudarnas organisation':
                return 'secondary';
            case 'corporation':
                return 'error';
            case 'division':
                return 'info';
            case 'department':
                return 'warning';
            case 'team':
                return 'success';
            case 'branch':
                return 'secondary';
            case 'unit':
                return 'warning';
            default:
                return 'default';
        }
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 0.5,
                    px: 1,
                    cursor: 'pointer',
                    borderRadius: 1,
                    ml: level * 2.5,
                    transition: 'background-color 0.15s',
                    fontWeight: level === 0 ? 600 : 400,
                    backgroundColor: isSelected
                        ? 'primary.light'
                        : 'transparent',
                    borderLeft: isSelected ? '3px solid' : 'none',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    '&:hover': {
                        backgroundColor: isSelected
                            ? 'primary.light'
                            : 'action.hover',
                    },
                }}
                onClick={handleOrganizationClick}
            >
                {/* Expand/Collapse Icon */}
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        mr: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {hasChildren ? (
                        <IconButton
                            size="small"
                            onClick={toggleExpanded}
                            sx={{
                                width: 20,
                                height: 20,
                                '&:hover': { backgroundColor: 'action.hover' },
                            }}
                        >
                            {isExpanded ? (
                                <ExpandMore sx={{ fontSize: 14 }} />
                            ) : (
                                <ChevronRight sx={{ fontSize: 14 }} />
                            )}
                        </IconButton>
                    ) : (
                        <Box sx={{ width: 20, height: 20 }} /> // Spacer for alignment
                    )}
                </Box>

                {/* Folder/Organization Icon */}
                <Box
                    sx={{
                        width: 20,
                        height: 20,
                        mr: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {hasChildren ? (
                        isExpanded ? (
                            <FolderOpen
                                sx={{ fontSize: 16, color: '#1976d2' }}
                            />
                        ) : (
                            <Folder sx={{ fontSize: 16, color: '#1976d2' }} />
                        )
                    ) : (
                        getTypeIcon(organization.organizationType)
                    )}
                </Box>

                {/* Organization Name and Type */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <Typography variant="body2">{organization.name}</Typography>
                    <Chip
                        label={organization.organizationType}
                        size="small"
                        color={getTypeColor(organization.organizationType)}
                        variant="outlined"
                        sx={{
                            height: 20,
                            fontSize: '0.75rem',
                            flexShrink: 0,
                        }}
                    />
                </Box>
            </Box>

            {/* Children */}
            {hasChildren && isExpanded && (
                <Box>
                    {organization.children.map((child) => (
                        <OrganizationNode
                            key={child.id}
                            organization={child}
                            level={level + 1}
                            onOrganizationClick={onOrganizationClick}
                            selectedId={selectedId}
                            parentId={organization.id} // Pass current organization's ID as parent
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

const TenantOrganizationsView = () => {
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const navigate = useNavigate();
    const [rootOrganizations, setRootOrganizations] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const organizationService = new OrganizationService();
    const setMessage = useGlobalStore((state) => state.setMessage);

    const handleOrganizationClick = (organization) => {
        setSelectedOrganization(organization);
    };

    const handleCreateChildOrganization = () => {
        if (
            selectedOrganization &&
            NavigationRoutes?.createTenantOrganizations
        ) {
            // Navigate to create page with parent organization ID as parameter
            navigate(
                `${NavigationRoutes.createTenantOrganizations}?parentId=${selectedOrganization.Id}&parentName=${encodeURIComponent(selectedOrganization.Name)}`
            );
        }
    };

    const handleUpdateOrganization = () => {
        if (
            selectedOrganization &&
            NavigationRoutes?.updateTenantOrganizations
        ) {
            // selectedOrganization.Id is not parent since we're editing and not creating a new one.
            navigate(
                `${NavigationRoutes.updateTenantOrganizations}?id=${selectedOrganization.Id}&parentId=${selectedOrganization.ParentOrganizationId}&childOrgs=${selectedOrganization.Children}&parentName=${encodeURIComponent(selectedOrganization.Name)}`
            );
        }
    };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        setIsLoading(true);
        try {
            let allOrganizations =
                await organizationService.getOrganizationTree();

            // Update to handle the new data format
            if (Array.isArray(allOrganizations)) {
                setRootOrganizations(allOrganizations);
            }
        } catch (error) {
            setMessage(error, 'warning');
        } finally {
            setIsLoading(false);
        }
    };

    const getTotalOrganizations = (orgs) => {
        let count = 0;
        orgs.forEach((org) => {
            count++; // Count the current organization
            // Updated to handle new data format
            if (org.children?.length > 0) {
                count += getTotalOrganizations(org.children);
            }
        });
        return count;
    };

    const totalCount = getTotalOrganizations(rootOrganizations);

    return (
        <Paper elevation={2} sx={{ maxWidth: 1000, mx: 'auto' }}>
            {/* Header */}
            <Box
                sx={{
                    bgcolor: 'grey.50',
                    px: 3,
                    py: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                    }}
                >
                    <Business />
                    <Typography variant="h6" component="h2">
                        Organisation Utforskaren
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Bläddra i din organisationshierarki - Klicka på en
                    organisation för att välja den
                </Typography>
            </Box>

            <Box sx={{ display: 'flex' }}>
                {/* Tree Content */}
                <Box
                    sx={{
                        flex: 1,
                        p: 1,
                        maxHeight: 400,
                        overflowY: 'auto',
                        borderRight: 1,
                        borderColor: 'divider',
                    }}
                >
                    {rootOrganizations.length > 0 ? (
                        rootOrganizations.map((org) => (
                            <OrganizationNode
                                key={org.id}
                                organization={org}
                                level={0}
                                onOrganizationClick={handleOrganizationClick}
                                selectedId={selectedOrganization?.Id}
                                parentId={null} // Root organizations have no parent
                            />
                        ))
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 8,
                                color: 'text.secondary',
                            }}
                        >
                            <Business
                                sx={{ fontSize: 48, mb: 2, color: 'grey.300' }}
                            />
                            <Typography>
                                {isLoading
                                    ? 'Loading organizations...'
                                    : 'Inga Organisationer funna'}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Details Panel */}
                <Box sx={{ width: 320, p: 3, bgcolor: 'grey.50' }}>
                    {selectedOrganization ? (
                        <Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <Business color="primary" />
                                <Typography variant="h6" component="h3">
                                    {selectedOrganization.Name}
                                </Typography>
                            </Box>

                            {/* Create Child Organization Button */}
                            <Box sx={{ mb: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Add />}
                                    onClick={handleCreateChildOrganization}
                                    fullWidth
                                    size="small"
                                >
                                    Skapa underorganisation
                                </Button>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<Update />}
                                    onClick={handleUpdateOrganization}
                                    fullWidth
                                    size="small"
                                >
                                    Updatera Organisation
                                </Button>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        Typ:
                                    </Typography>
                                    <Chip
                                        label={
                                            selectedOrganization.OrganizationType
                                        }
                                        color={(() => {
                                            switch (
                                                selectedOrganization.OrganizationType?.toLowerCase()
                                            ) {
                                                case 'root':
                                                    return 'error';
                                                case 'default':
                                                    return 'primary';
                                                case 'asagudarna':
                                                    return 'success';
                                                case 'gudarnas organisation':
                                                    return 'secondary';
                                                case 'corporation':
                                                    return 'error';
                                                case 'division':
                                                    return 'info';
                                                case 'department':
                                                    return 'warning';
                                                case 'team':
                                                    return 'success';
                                                case 'branch':
                                                    return 'secondary';
                                                case 'unit':
                                                    return 'warning';
                                                default:
                                                    return 'default';
                                            }
                                        })()}
                                        size="small"
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        ID:
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1,
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            {selectedOrganization.Id}
                                        </Typography>
                                    </Paper>
                                </Box>

                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        Föräldra ID:
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1,
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            {selectedOrganization.ParentOrganizationId ||
                                                'Root Organization'}
                                        </Typography>
                                    </Paper>
                                </Box>

                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        Barn Organisationer:
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1,
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {selectedOrganization.Children
                                                ?.length || 0}
                                        </Typography>
                                    </Paper>
                                </Box>

                                {selectedOrganization.CreatedOn && (
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Skapad:
                                        </Typography>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1,
                                                bgcolor: 'background.paper',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: '0.75rem' }}
                                            >
                                                {new Date(
                                                    selectedOrganization.CreatedOn
                                                ).toLocaleDateString()}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                color: 'text.secondary',
                                mt: 8,
                            }}
                        >
                            <Business
                                sx={{ fontSize: 64, mb: 2, color: 'grey.300' }}
                            />
                            <Typography variant="body2">
                                Välj en organisation för att se detaljer
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Footer with summary */}
            <Box
                sx={{
                    bgcolor: 'grey.50',
                    px: 3,
                    py: 1.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    • {totalCount} organisationer totalt
                </Typography>
                {selectedOrganization && (
                    <Typography
                        variant="caption"
                        color="primary.main"
                        sx={{ fontWeight: 500 }}
                    >
                        Selected: {selectedOrganization.Name}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

export default TenantOrganizationsView;
